import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  BarChart3,
  Users,
  TrendingUp,
  TrendingDown,
  Star,
  ChevronDown,
  ChevronUp,
  Plus,
} from 'lucide-react';

interface QbrReport {
  id: string;
  client_id: string;
  client_name: string;
  period: string;
  vehicles_used: string[];
  total_visits: number;
  total_miles: number;
  total_cost: number;
  fuel_cost: number;
  maintenance_cost: number;
  cost_per_mile: number;
  cost_trend_pct: number;
  utilization_pct: number;
  safety_score_avg: number;
  recommendations: string[];
  generated_by: 'manual' | 'auto';
  created_by?: string;
  created_at: string;
}

interface ReportList {
  data: QbrReport[];
  total: number;
}

const MOCK_REPORTS: QbrReport[] = [];

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR];

export default function QbrReportsPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formError, setFormError] = useState('');

  // Form state
  const [clientName, setClientName] = useState('');
  const [clientId, setClientId] = useState('');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState('Q1');

  const { data: listData, isLoading, refetch } = useApi<ReportList>('/api/qbr-reports');
  const reports = listData?.data ?? MOCK_REPORTS;

  // Summary stats
  const uniqueClients = new Set(reports.map(r => r.client_id)).size;
  const avgTrend = reports.length > 0
    ? Math.round(reports.reduce((s, r) => s + r.cost_trend_pct, 0) / reports.length * 10) / 10
    : 0;
  const topClient = reports.reduce<QbrReport | null>((top, r) => (!top || r.total_cost > top.total_cost ? r : top), null);

  async function handleGenerate() {
    setFormError('');
    if (!clientName.trim()) { setFormError('Client name is required'); return; }

    const cid = clientId.trim() || clientName.trim().toLowerCase().replace(/\s+/g, '-');
    const period = `${year}-${quarter}`;

    setGenerating(true);
    try {
      const res = await fetch('/api/qbr-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: cid, client_name: clientName.trim(), period }),
      });
      if (!res.ok) {
        const err = await res.json() as { message?: string };
        setFormError(err.message ?? 'Failed to generate report');
        return;
      }
      setShowForm(false);
      setClientName('');
      setClientId('');
      refetch?.();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fleet QBR Generator"
        description="Quarterly business review reports per client"
        actions={
          <div className="flex items-center gap-2">
            {isLoading && <Spinner size={16} />}
            <button
              onClick={() => setShowForm(v => !v)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              <Plus className="w-4 h-4" />
              Generate QBR
            </button>
          </div>
        }
      />

      {/* Generate Form */}
      {showForm && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Generate New QBR Report</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>
                  Client Name *
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                  placeholder="Acme Corp"
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>
                  Client ID (optional)
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                  placeholder="auto-generated"
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>
                  Year
                </label>
                <select
                  value={year}
                  onChange={e => setYear(parseInt(e.target.value, 10))}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                >
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>
                  Quarter
                </label>
                <select
                  value={quarter}
                  onChange={e => setQuarter(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                  style={{ background: 'var(--input)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                >
                  {QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
            </div>
            {formError && (
              <p className="text-xs" style={{ color: '#EF4444' }}>{formError}</p>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => void handleGenerate()}
                disabled={generating}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ background: 'var(--primary)', color: '#fff', opacity: generating ? 0.6 : 1 }}
              >
                {generating ? 'Generating…' : `Generate ${year}-${quarter}`}
              </button>
              <button
                onClick={() => { setShowForm(false); setFormError(''); }}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ background: 'var(--glass-bg)', color: 'var(--muted-foreground)', border: '1px solid var(--glass-border)' }}
              >
                Cancel
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={BarChart3} label="Reports Generated" value={reports.length} />
        <StatCard icon={Users} label="Clients Covered" value={uniqueClients} />
        <div
          className="rounded-xl p-4 flex flex-col gap-1"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}
        >
          <div className="flex items-center gap-2">
            {avgTrend >= 0
              ? <TrendingUp className="w-4 h-4" style={{ color: '#EF4444' }} />
              : <TrendingDown className="w-4 h-4" style={{ color: '#22C55E' }} />}
            <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
              Avg Cost Trend
            </span>
          </div>
          <span
            className="text-2xl font-bold"
            style={{ color: avgTrend > 5 ? '#EF4444' : avgTrend < -5 ? '#22C55E' : 'var(--foreground)' }}
          >
            {avgTrend >= 0 ? '+' : ''}{avgTrend}%
          </span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>vs prior period</span>
        </div>
        <div
          className="rounded-xl p-4 flex flex-col gap-1"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}
        >
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4" style={{ color: '#EAB308' }} />
            <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
              Top Client
            </span>
          </div>
          <span className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
            {topClient?.client_name ?? '—'}
          </span>
          {topClient && (
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              ${topClient.total_cost.toLocaleString()} total
            </span>
          )}
        </div>
      </div>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>QBR Reports</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size={24} />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2" style={{ color: 'var(--muted-foreground)' }}>
              <BarChart3 className="w-8 h-8 opacity-40" />
              <p className="text-sm">No QBR reports yet — generate one to get started.</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
              {reports.map(report => {
                const isExpanded = expandedId === report.id;
                const trendColor = report.cost_trend_pct > 5
                  ? '#EF4444'
                  : report.cost_trend_pct < -5
                    ? '#22C55E'
                    : 'var(--muted-foreground)';

                return (
                  <div key={report.id}>
                    {/* Row */}
                    <button
                      className="w-full text-left"
                      onClick={() => setExpandedId(isExpanded ? null : report.id)}
                    >
                      <div className="grid grid-cols-7 gap-2 px-4 py-3 hover:bg-white/5 transition-colors text-sm">
                        <div className="col-span-2">
                          <p className="font-medium" style={{ color: 'var(--foreground)' }}>{report.client_name}</p>
                          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{report.period}</p>
                        </div>
                        <div className="flex items-center" style={{ color: 'var(--muted-foreground)' }}>
                          {report.vehicles_used.length} vehicle{report.vehicles_used.length !== 1 ? 's' : ''}
                        </div>
                        <div className="flex items-center font-mono" style={{ color: 'var(--foreground)' }}>
                          {report.total_visits}
                        </div>
                        <div className="flex items-center font-mono" style={{ color: 'var(--foreground)' }}>
                          ${report.total_cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                        <div className="flex items-center font-mono" style={{ color: trendColor }}>
                          {report.cost_trend_pct >= 0 ? '+' : ''}{report.cost_trend_pct}%
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {report.generated_by === 'auto' ? 'Auto' : 'Manual'}
                          </span>
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                            : <ChevronDown className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />}
                        </div>
                      </div>
                    </button>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div
                        className="px-4 pb-4 pt-2 space-y-4"
                        style={{ background: 'rgba(0,0,0,0.15)', borderTop: '1px solid var(--glass-border)' }}
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                          {[
                            { label: 'Total Miles', value: report.total_miles.toLocaleString() },
                            { label: 'Fuel Cost', value: `$${report.fuel_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                            { label: 'Maintenance', value: `$${report.maintenance_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                            { label: 'Cost / Mile', value: `$${report.cost_per_mile.toFixed(2)}` },
                            { label: 'Safety Score', value: `${report.safety_score_avg}/100` },
                          ].map(item => (
                            <div key={item.label}>
                              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.label}</p>
                              <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--foreground)' }}>{item.value}</p>
                            </div>
                          ))}
                        </div>

                        {report.recommendations.length > 0 && (
                          <div>
                            <p className="text-xs font-medium mb-2" style={{ color: 'var(--muted-foreground)' }}>RECOMMENDATIONS</p>
                            <ul className="space-y-1">
                              {report.recommendations.map((rec, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--foreground)' }}>
                                  <span style={{ color: '#10B981' }}>•</span>
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          Generated {new Date(report.created_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
